import { type BlockNoteEditor } from '@blocknote/core';

/**
 * Utility function to copy rich text from a BlockNote editor to clipboard
 * @param editor The BlockNote editor instance
 * @param fallbackMarkdown Optional fallback markdown string if HTML conversion fails
 * @param includeHtml Whether to include HTML formatting (default: true)
 * @returns Promise<boolean> indicating success
 */
export const copyFromEditor = async (
  editor: BlockNoteEditor,
  fallbackMarkdown?: string,
  includeHtml: boolean = true,
): Promise<boolean> => {
  try {
    // Get HTML representation of the content from the editor
    const htmlContent = await editor.blocksToHTMLLossy(editor.document);

    // Get markdown as fallback
    const markdown = fallbackMarkdown || (await editor.blocksToMarkdownLossy(editor.document));

    // Use the existing copyRichText utility from utils
    const { copyRichText } = await import('../utils');
    return await copyRichText(htmlContent, markdown, includeHtml);
  } catch (err) {
    console.error('Error copying from editor:', err);

    // Fallback to copying plain markdown
    if (fallbackMarkdown) {
      try {
        await navigator.clipboard.writeText(fallbackMarkdown);
        return true;
      } catch (clipboardErr) {
        console.error('Plain text copy also failed:', clipboardErr);
        return false;
      }
    }

    return false;
  }
};
